import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Circle } from 'react-native-svg';
import { useSelector } from 'react-redux';

import HeaderChipPng from '../../../assets/chip.png';
import { RewardChipPng } from '../../../assets/event';
import PlayerIconPng from '../../../assets/header/player-icon.png';
import VipCrownPng from '../../../assets/profileRedesign/vip-crown.png';
import { testProps } from '../../../config';
import { goldGlow } from '../../../shared/glowStyles';
import { RootState } from '../../../store';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { getAvatarUrl } from '../../../utils/axiosInstance';
import { useDevice } from '../../../utils/hooks/useDevice';
import { LoginButton } from '../../Components';
import { TouchableOpacity } from '../../Components/common/Touchable';
import {
	HEADER_ACTIONS_WIDTH,
	HEADER_BAR,
	HEADER_WALLET_GAP,
} from '../../Components/Top/headerBar';
import { RootStackParamList } from '../../navigator';

type Nav = NativeStackNavigationProp<RootStackParamList>;

// Figma "Level Badge" (5307:47094): a 2.73-wide ring on a 26 disc - #220A01 track, the progress in
// #4ABF54 from 12 o'clock clockwise - round a 20 #F5C747 disc with a black hairline.
const LEVEL_TRACK_COLOR = '#220A01';
const LEVEL_PROGRESS_COLOR = '#4ABF54';
const LEVEL_RING_WIDTH = 2.73;
const LEVEL_DISC_SIZE = 20;
// Figma "Wallet" Row: the chip art sits low in its 32x43 slot, 9.17 from the top - not centred.
const WALLET_CHIP = { width: 32.43, height: 33.05, top: 9.17, slotWidth: 32, slotHeight: 43 };
const AVATAR_SIZE = 56;
const LEVEL_BADGE_SIZE = 26;
const HEADER_FONT_FAMILY = 'Sofia Sans';

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const formatChips = (chips: number) => new Intl.NumberFormat('de-DE').format(chips);

/**
 * How far through the current level the player is. `auth.statistics` is not used: it is only
 * loaded when a profile opens, and holds whichever player was opened last.
 */
const getLevelProgress = (levelXp?: number, levelXpToNext?: number) =>
	levelXpToNext && levelXpToNext > 0 ? clamp((levelXp ?? 0) / levelXpToNext, 0, 1) : 0;

type ProfileProps = {
	/**
	 * Just the avatar and its level badge, drawn at the home redesign's 54 - the desktop top bar
	 * puts the balance in its own centred wallet instead of the pill beside the avatar.
	 */
	avatarOnly?: boolean;
};

const Profile = ({ avatarOnly = false }: ProfileProps = {}) => {
	const { navigate } = useNavigation<Nav>();
	const isLogged = useSelector(selectIsLogged);
	const player = useSelector((state: RootState) => state.auth.user);
	const { isMobile, isWeb } = useDevice();
	// The design's top bar is the same on every phone screen, so its sizes are too - not just Home's.
	const isHomeRedesign = avatarOnly || isMobile;
	const isBar = isHomeRedesign && !avatarOnly;
	const currentLevel = player?.level ?? 1;
	const avatarUri =
		player?.avatarFileId && typeof player.avatarFileId === 'string'
			? getAvatarUrl(player.avatarFileId)
			: undefined;
	const hasCustomAvatar = Boolean(avatarUri);
	const avatarSource = avatarUri ? { uri: avatarUri } : PlayerIconPng;
	const chipsLabel = formatChips(player?.chips ?? 0);
	const progress = getLevelProgress(player?.levelXp, player?.levelXpToNext);

	if (!isLogged) {
		return <LoginButton style={styles.loginButton} />;
	}

	return (
		<View style={[styles.row, isBar && styles.rowBar]}>
			<TouchableOpacity
				onPress={() => navigate('Profile')}
				activeOpacity={0.9}
				{...testProps('cta-profile-open')}
				style={[
					styles.button,
					isHomeRedesign && styles.buttonHomeRedesign,
					isWeb && styles.buttonWeb,
				]}
			>
				<View style={[styles.avatarWrap, isHomeRedesign && styles.avatarWrapHomeRedesign]}>
					{player?.isVip ? (
						<Image source={VipCrownPng} resizeMode='contain' style={styles.vipCrown} />
					) : null}
					<View style={[styles.avatarFrame, isHomeRedesign && styles.avatarFrameHomeRedesign]}>
						<Image
							source={avatarSource}
							style={[
								hasCustomAvatar ? styles.avatarImage : styles.avatarPlaceholderImage,
								isHomeRedesign && styles.avatarImageHomeRedesign,
							]}
							resizeMode={hasCustomAvatar ? 'cover' : 'contain'}
						/>
					</View>

					<View style={styles.levelBadge}>
						<LevelProgressRing progress={progress} />
						<View style={styles.levelBadgeInner}>
							<Text numberOfLines={1} style={styles.levelText}>
								{currentLevel}
							</Text>
						</View>
					</View>
				</View>
			</TouchableOpacity>

			{avatarOnly ? null : (
				<View
					style={[
						styles.chipsWrap,
						isHomeRedesign && styles.chipsWrapHomeRedesign,
						isBar && styles.chipsWrapBar,
					]}
				>
					<TouchableOpacity
						onPress={() => navigate('BuyPackages', { activeTab: 'chips' })}
						activeOpacity={0.9}
						style={[
							styles.chipsPill,
							isHomeRedesign && styles.chipsPillHomeRedesign,
							isWeb && styles.buttonWeb,
						]}
					>
						{isHomeRedesign ? (
							<View style={styles.chipSlot}>
								<Image source={RewardChipPng} style={styles.chipSlotIcon} resizeMode='contain' />
							</View>
						) : (
							<Image source={HeaderChipPng} style={styles.chipIcon} resizeMode='contain' />
						)}
						<Text
							numberOfLines={1}
							style={[
								styles.chipsText,
								isHomeRedesign ? styles.chipsTextHomeRedesign : styles.chipsTextLegacy,
							]}
						>
							{chipsLabel}
						</Text>
					</TouchableOpacity>

					<TouchableOpacity
						onPress={() => navigate('BuyPackages', { activeTab: 'chips' })}
						activeOpacity={0.9}
						style={[
							styles.plusButton,
							isHomeRedesign && styles.plusButtonHomeRedesign,
							isWeb && styles.buttonWeb,
						]}
					>
						<View style={styles.plusIcon}>
							<View style={styles.plusVertical} />
							<View style={styles.plusHorizontal} />
						</View>
					</TouchableOpacity>
				</View>
			)}
		</View>
	);
};

const styles = StyleSheet.create({
	row: {
		flexDirection: 'row',
		alignItems: 'center',
		gap: 12,
	},
	// Figma "Top Bar": the avatar at the bar's left end and the wallet right-aligned against the
	// bell and settings buttons, 10 before them, whatever the screen's width.
	rowBar: {
		alignSelf: 'stretch',
		height: HEADER_BAR.height,
	},
	button: {
		width: AVATAR_SIZE,
		height: AVATAR_SIZE,
		alignItems: 'center',
		justifyContent: 'center',
	},
	buttonHomeRedesign: {
		width: 54,
		height: 54,
	},
	buttonWeb: {
		cursor: 'pointer',
	},
	// Figma Top Bar (5911:55875): LOG IN sits 4 into the 54-high bar, beside the settings button.
	loginButton: {
		marginTop: 4,
	},
	avatarWrap: {
		width: AVATAR_SIZE,
		height: AVATAR_SIZE,
		justifyContent: 'center',
		alignItems: 'center',
	},
	avatarWrapHomeRedesign: {
		width: 54,
		height: 54,
	},
	avatarFrame: {
		width: AVATAR_SIZE,
		height: AVATAR_SIZE,
		borderRadius: AVATAR_SIZE / 2,
		overflow: 'hidden',
		justifyContent: 'center',
		alignItems: 'center',
		borderWidth: 1,
		borderColor: '#F5C747',
		...goldGlow,
	},
	avatarFrameHomeRedesign: {
		width: 54,
		height: 54,
		borderRadius: 27,
	},
	avatarImage: {
		width: AVATAR_SIZE,
		height: AVATAR_SIZE,
	},
	avatarPlaceholderImage: {
		width: AVATAR_SIZE,
		height: AVATAR_SIZE,
	},
	avatarImageHomeRedesign: {
		width: 54,
		height: 54,
		borderRadius: 27,
	},
	vipCrown: {
		position: 'absolute',
		top: -12,
		left: 12,
		width: 30,
		height: 20,
		zIndex: 2,
	},
	levelBadge: {
		position: 'absolute',
		left: -4,
		bottom: -2,
		width: LEVEL_BADGE_SIZE,
		height: LEVEL_BADGE_SIZE,
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 3,
	},
	levelBadgeInner: {
		position: 'absolute',
		width: LEVEL_DISC_SIZE,
		height: LEVEL_DISC_SIZE,
		alignItems: 'center',
		justifyContent: 'center',
	},
	// Figma "Label/Badge": Sofia Sans Black 10/1.2, text/on-badge.
	levelText: {
		color: '#000000',
		fontFamily: HEADER_FONT_FAMILY,
		fontSize: 10,
		lineHeight: 12,
		fontWeight: '900',
		includeFontPadding: false,
	},
	chipsWrap: {
		width: 166,
		height: 49,
		justifyContent: 'center',
	},
	chipsWrapHomeRedesign: {
		width: 161,
		height: 43,
	},
	chipsWrapBar: {
		marginLeft: 'auto',
		marginRight: HEADER_ACTIONS_WIDTH + HEADER_WALLET_GAP,
	},
	chipsPill: {
		width: 166,
		height: 49,
		paddingLeft: 14,
		paddingRight: 42,
		borderRadius: 24.5,
		borderWidth: 1,
		borderColor: '#F5C747',
		backgroundColor: '#290908E0',
		flexDirection: 'row',
		alignItems: 'center',
		gap: 8,
		...goldGlow,
	},
	chipsPillHomeRedesign: {
		width: 161,
		height: 43,
		borderRadius: 21.5,
		paddingLeft: 5,
		// The + button is 24 wide, 9 in from the right; 41 leaves the same 8 before it as the
		// pill's gap leaves after the chip, so the balance centres between the two.
		paddingRight: 41,
	},
	chipIcon: {
		width: 23,
		height: 23,
	},
	chipSlot: {
		width: WALLET_CHIP.slotWidth,
		height: WALLET_CHIP.slotHeight,
	},
	chipSlotIcon: {
		position: 'absolute',
		left: 0,
		top: WALLET_CHIP.top,
		width: WALLET_CHIP.width,
		height: WALLET_CHIP.height,
	},
	chipsText: {
		color: '#F5C747',
		fontFamily: HEADER_FONT_FAMILY,
		fontSize: 15,
		fontWeight: '800',
		letterSpacing: 0.3,
	},
	chipsTextLegacy: {
		maxWidth: 84,
	},
	// The balance takes the room between the chip and the + button and centres in it, so it never
	// hugs the chip.
	chipsTextHomeRedesign: {
		flex: 1,
		textAlign: 'center',
		fontSize: 15,
		fontWeight: '700',
		letterSpacing: -0.26,
	},
	plusButton: {
		position: 'absolute',
		right: 13,
		width: 23,
		height: 23,
		borderRadius: 11.5,
		backgroundColor: '#55C84F',
		alignItems: 'center',
		justifyContent: 'center',
		...goldGlow,
	},
	plusButtonHomeRedesign: {
		right: 9,
		width: 24,
		height: 24,
		borderRadius: 12,
	},
	plusIcon: {
		width: 10,
		height: 10,
		alignItems: 'center',
		justifyContent: 'center',
	},
	plusVertical: {
		position: 'absolute',
		width: 3,
		height: 10,
		borderRadius: 1.5,
		backgroundColor: '#FFFFFF',
	},
	plusHorizontal: {
		position: 'absolute',
		width: 10,
		height: 3,
		borderRadius: 1.5,
		backgroundColor: '#FFFFFF',
	},
	root: {
		width: LEVEL_BADGE_SIZE,
		height: LEVEL_BADGE_SIZE,
		alignItems: 'center',
		justifyContent: 'center',
	},
});

/**
 * One SVG rather than two clipped, rotated half-rings: the halves left a hairline seam where they
 * met, which showed as a dotted line across the badge.
 */
function LevelProgressRing({ progress }: { progress: number }) {
	const centre = LEVEL_BADGE_SIZE / 2;
	const radius = (LEVEL_BADGE_SIZE - LEVEL_RING_WIDTH) / 2;
	const circumference = 2 * Math.PI * radius;
	const arc = clamp(progress, 0, 1) * circumference;
	// The disc's black hairline sits right on the ring's inner edge, with no gap between them.
	const discRadius = radius - LEVEL_RING_WIDTH / 2 - 0.5;

	return (
		<View style={styles.root}>
			<Svg width={LEVEL_BADGE_SIZE} height={LEVEL_BADGE_SIZE}>
				<Circle
					cx={centre}
					cy={centre}
					r={radius}
					stroke={LEVEL_TRACK_COLOR}
					strokeWidth={LEVEL_RING_WIDTH}
					fill='none'
				/>
				{arc > 0 ? (
					<Circle
						cx={centre}
						cy={centre}
						r={radius}
						stroke={LEVEL_PROGRESS_COLOR}
						strokeWidth={LEVEL_RING_WIDTH}
						strokeDasharray={`${arc} ${circumference}`}
						fill='none'
						transform={`rotate(-90 ${centre} ${centre})`}
					/>
				) : null}
				<Circle
					cx={centre}
					cy={centre}
					r={discRadius}
					fill='#F5C747'
					stroke='#000000'
					strokeWidth={1}
				/>
			</Svg>
		</View>
	);
}

export default Profile;
